/**
 * TelePortal schemas
 */

import { z } from 'zod';

/**
 * The household member vocabulary is `HOUSEHOLD_MEMBER_TYPES` in
 * `@tonnie/foundation`; it is restated here because this package depends on
 * zod alone (the same rule the evidence redaction vocabulary follows).
 * `household-vocabulary.spec.ts` in platform-api asserts the two lists are
 * identical, so they cannot drift.
 */
const HOUSEHOLD_MEMBER_TYPES = [
  'talent',
  'partner',
  'child',
  'pet',
  'extended_family',
  'other',
] as const;

/**
 * The task duration vocabulary is `TELEPORTAL_TASK_DURATIONS` in
 * `@tonnie/types`; it is restated here for the same reason the household
 * vocabulary is — this package depends on zod alone. `teleportal-vocabulary.spec.ts`
 * in platform-api asserts the two lists are identical, so they cannot drift.
 *
 * This is the boundary that was `z.string()`: the brain could hand platform-api
 * any duration at all and it was persisted, so `3-5 days` reached 25 production
 * tasks with no key in any locale.
 */
export const TASK_DURATIONS = [
  '1 day',
  '1-2 days',
  '1-3 days',
  '3-5 days',
  '1 week',
  '1-2 weeks',
  '2-3 weeks',
  '2-4 weeks',
  '2-6 weeks',
  '2-8 weeks',
  '4-8 weeks',
  '4-12 weeks',
  'ongoing',
] as const;

/**
 * Complete task schema
 */
export const completeTaskSchema = z.object({
  taskId: z.uuid(),
  completionNotes: z.string().max(2000).optional(),
  documentIds: z.array(z.uuid()).optional(),
  responses: z.record(z.string(), z.unknown()).optional(),
  attachmentIds: z.array(z.uuid()).optional(),
});

/**
 * Upload document schema
 */
export const uploadDocumentSchema = z.object({
  fileId: z.uuid(),
  taskId: z.uuid().optional(),
  trackId: z.uuid().optional(),
  documentType: z.string().min(1).max(100),
  documentName: z.string().min(1).max(255),
  description: z.string().max(1000).optional(),
  mimeType: z.string().max(100).optional(),
  fileSize: z.number().int().positive().optional(),
});

/**
 * Validate document schema
 */
export const validateDocumentSchema = z.object({
  fileId: z.uuid(),
  validationResult: z.enum(['valid', 'invalid', 'expired']),
  validationNotes: z.string().max(500).optional(),
});

/**
 * Generate package schema
 */
export const generatePackageSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(1000).optional(),
  documentIds: z.array(z.uuid()).min(1),
  packageType: z.enum(['visa_application', 'work_permit', 'residence', 'custom']),
});

/**
 * Share package schema
 */
export const sharePackageSchema = z.object({
  recipientEmail: z.email(),
  expiresInDays: z.number().int().min(1).max(90).optional().default(7),
  message: z.string().max(1000).optional(),
});

// ═══════════════════════════════════════════════════════════════════════
// SETUP PROFILE
// ═══════════════════════════════════════════════════════════════════════

export const saveSetupProfileSchema = z.object({
  dimensions: z.object({
    household: z.record(z.string(), z.unknown()),
    legalStatus: z.record(z.string(), z.unknown()),
    housing: z.record(z.string(), z.unknown()),
    travel: z.record(z.string(), z.unknown()),
    professional: z.record(z.string(), z.unknown()),
    vaultScan: z.record(z.string(), z.unknown()),
    timeline: z.record(z.string(), z.unknown()),
    specialCircumstances: z.record(z.string(), z.unknown()),
  }),
  origin: z.string().min(2).max(3),
  destination: z.string().min(2).max(3),
  hasSponsor: z.boolean(),
});

export const refreshSetupSchema = z.object({
  dimensions: z.record(z.string(), z.unknown()),
  origin: z.string().min(2).max(3),
  destination: z.string().min(2).max(3),
  hasSponsor: z.boolean(),
});

const brainTaskSchema = z.object({
  template_id: z.string(),
  track: z.string(),
  task_code: z.string(),
  task_name_key: z.string(),
  description_key: z.string(),
  task_type: z.string(),
  household_scope: z.string(),
  ownership_mode: z.string(),
  is_company_task: z.boolean(),
  priority: z.string(),
  is_blocking: z.boolean(),
  is_required: z.boolean(),
  requires_document: z.boolean().optional(),
  required_document_types: z.array(z.string()).optional(),
  estimated_duration: z.enum(TASK_DURATIONS),
  miles_reward: z.number(),
  order_index: z.number(),
  depends_on: z.array(z.string()),
  household_member_id: z.string().nullable().optional(),
  household_member_name: z.string().nullable().optional(),
  household_member_type: z.string().nullable().optional(),
});

const brainTrackSchema = z.object({
  track_code: z.string(),
  track_name_key: z.string(),
  is_foundation: z.boolean(),
  order_index: z.number(),
  icon: z.string().optional(),
  color: z.string().optional(),
});

const brainMilestoneSchema = z.object({
  template_id: z.string(),
  track: z.string(),
  title_key: z.string(),
  order_index: z.number(),
  tasks_required: z.number(),
  required_task_codes: z.array(z.string()).optional(),
});

const relocationKitSchema = z.object({
  tracks: z.array(brainTrackSchema),
  tasks: z.array(brainTaskSchema),
  milestones: z.array(brainMilestoneSchema),
  dependency_graph: z.array(z.object({ from: z.string(), to: z.string() })).optional(),
  critical_path: z.array(z.string()).optional(),
  complexity: z.object({
    score: z.number(),
    level: z.enum(['simple', 'standard', 'complex']),
    factors: z.array(z.string()),
  }),
  metadata: z.record(z.string(), z.unknown()),
});

export const activateKitSchema = z.object({
  dimensions: z.record(z.string(), z.unknown()),
  origin: z.string().min(2).max(3),
  destination: z.string().min(2).max(3),
  hasSponsor: z.boolean(),
  kit: relocationKitSchema,
  modifications: z
    .object({
      removed_task_codes: z.array(z.string()),
    })
    .optional(),
});

// ═══════════════════════════════════════════════════════════════════════
// HOUSEHOLD MEMBERS
// ═══════════════════════════════════════════════════════════════════════

const _createHouseholdMemberBase = z.object({
  type: z.enum(HOUSEHOLD_MEMBER_TYPES),
  /** Stable, client-minted identity (0134). `'self'` is the applicant. */
  clientRef: z.string().min(1).max(100).optional(),
  /** Legacy single display name. Prefer `firstName` / `lastName`. */
  name: z.string().min(1).max(255).optional(),
  firstName: z.string().min(1).max(100).optional(),
  lastName: z.string().max(100).optional(),
  nationality: z.string().max(3).optional(),
  dateOfBirth: z.string().optional(),
  schoolStage: z.enum(['pre_school', 'primary', 'secondary', 'higher']).optional(),
  profession: z.string().max(255).optional(),
  petSpecies: z.enum(['dog', 'cat', 'other']).optional(),
  petBreed: z.string().max(100).optional(),
  relationship: z.string().max(50).optional(),
  dependencyStatus: z.enum(['dependent', 'independent']).optional(),
});

export const createHouseholdMemberSchema = _createHouseholdMemberBase.refine(
  (v) => Boolean(v.name?.trim() || v.firstName?.trim()),
  { message: 'name or firstName is required', path: ['name'] }
);

export const updateHouseholdMemberSchema = _createHouseholdMemberBase.partial();

// ═══════════════════════════════════════════════════════════════════════
// TASK FILTERS & REOPEN
// ═══════════════════════════════════════════════════════════════════════

export const taskFilterSchema = z.object({
  householdMemberId: z.uuid().optional(),
  ownershipMode: z.enum(['talent', 'company', 'government', 'dual']).optional(),
  isCompanyTask: z.boolean().optional(),
  status: z.string().optional(),
});

export const reopenTaskSchema = z.object({
  reason: z.string().max(500).optional(),
});

// Type exports
export type CompleteTaskInput = z.infer<typeof completeTaskSchema>;
export type UploadDocumentInput = z.infer<typeof uploadDocumentSchema>;
export type ValidateDocumentInput = z.infer<typeof validateDocumentSchema>;
export type GeneratePackageInput = z.infer<typeof generatePackageSchema>;
export type SharePackageInput = z.infer<typeof sharePackageSchema>;
export type SaveSetupProfileInput = z.infer<typeof saveSetupProfileSchema>;
export type RefreshSetupInput = z.infer<typeof refreshSetupSchema>;
export type ActivateKitInput = z.infer<typeof activateKitSchema>;
export type CreateHouseholdMemberInput = z.infer<typeof createHouseholdMemberSchema>;
export type UpdateHouseholdMemberInput = z.infer<typeof updateHouseholdMemberSchema>;
export type TaskFilterInput = z.infer<typeof taskFilterSchema>;
export type ReopenTaskInput = z.infer<typeof reopenTaskSchema>;
